// packages/vigilo-app/src/data/useMockData.ts
// Mock equivalents of the live data hooks, for UI development without
// a running Core or Simulation backend.
//
// Toggle via the sidebar Data Source switch (dev only) or Admin → Simulation Control.
// State is held in MockModeContext — no page reload required.

import { useCallback, useEffect, useState } from 'react';
import type { DensitySnapshot, Alert, Zone } from '@vigilo/shared';
import { MOCK_DENSITY, MOCK_ALERTS, MOCK_ZONES } from './mock';
import type { DensityStreamState } from '../hooks/useDensityStream';
import { EMPTY_ALERT_STREAM_SUMMARY, type AlertStreamOptions, type AlertStreamState } from '../hooks/useAlertStream';
import { useDensityStream } from '../hooks/useDensityStream';
import { useAlertStream } from '../hooks/useAlertStream';
import { useMockMode } from '@/shell/MockModeContext';

// ─── useMockDensityStream ─────────────────────────────────────────────────────
// Returns MOCK_DENSITY with subtle deterministic drift every 2 seconds
// to simulate live data movement without WebSocket dependency.

export function useMockDensityStream(): DensityStreamState {
  const [snapshots, setSnapshots] = useState<Record<string, DensitySnapshot>>(
    () => ({ ...MOCK_DENSITY })
  );

  useEffect(() => {
    const id = setInterval(() => {
      setSnapshots(prev => {
        const next = { ...prev };
        const tick = Math.floor(Date.now() / 2000);
        for (const zoneId of Object.keys(next)) {
          const snap = next[zoneId];
          const phase = Array.from(zoneId).reduce((sum, char) => sum + char.charCodeAt(0), 0) % 31;
          const drift = Math.sin((tick + phase) / 5) * 0.4;
          const newUtil = Math.min(100, Math.max(0, Math.round((snap.capacityUtilisation + drift) * 10) / 10));
          const base = MOCK_DENSITY[zoneId];
          const newCount = Math.round((base?.occupancyCount ?? 0) * (newUtil / (base?.capacityUtilisation ?? 1)));
          next[zoneId] = {
            ...snap,
            capacityUtilisation: newUtil,
            densityScore:        newUtil,
            occupancyCount:      newCount,
            timestamp:           new Date().toISOString(),
          };
        }
        return next;
      });
    }, 2000);

    return () => clearInterval(id);
  }, []);

  return { snapshots, connected: true, source: 'mock' as const, lastEventAt: null, eventsReceived: 0 };
}

// ─── useMockAlertStream ───────────────────────────────────────────────────────
// Returns MOCK_ALERTS with local-state lifecycle mutations.
// acknowledge/escalate/resolve update state only — no API calls.

export interface MockAlertCounts {
  open:         number;
  acknowledged: number;
  escalated:    number;
  resolved:     number;
}

export interface MockAlertStreamState extends AlertStreamState {
  counts: MockAlertCounts;
}

export function useMockAlertStream(operationId?: string | null, options: AlertStreamOptions = {}): MockAlertStreamState {
  const [alerts, setAlerts] = useState<Alert[]>(() => [...MOCK_ALERTS]);
  const enabled = options.enabled ?? true;
  const requireOperation = options.requireOperation ?? false;
  const zoneIds = options.zoneIds?.length ? new Set(options.zoneIds) : null;
  const scopedAlerts = enabled && (!requireOperation || operationId)
    ? alerts.filter(alert => (!operationId || alert.operationId === operationId) && (!zoneIds || zoneIds.has(alert.zoneId)))
    : [];
  const connected = enabled && (!requireOperation || Boolean(operationId));
  const now = new Date().toISOString();

  const patch = (alertId: string, updates: Partial<Alert>) => {
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, ...updates } : a));
  };

  const acknowledge = useCallback(async (alertId: string) => {
    patch(alertId, { status: 'acknowledged', acknowledgedAt: new Date().toISOString() });
  }, []);

  const escalate = useCallback(async (alertId: string) => {
    patch(alertId, { status: 'escalated', escalatedAt: new Date().toISOString() });
  }, []);

  const resolve = useCallback(async (alertId: string, note: string) => {
    patch(alertId, { status: 'resolved', resolvedAt: new Date().toISOString(), resolutionNote: note.trim() });
  }, []);

  const counts: MockAlertCounts = {
    open:         scopedAlerts.filter(a => a.status === 'open').length,
    acknowledged: scopedAlerts.filter(a => a.status === 'acknowledged').length,
    escalated:    scopedAlerts.filter(a => a.status === 'escalated').length,
    resolved:     scopedAlerts.filter(a => a.status === 'resolved').length,
  };

  return {
    alerts: scopedAlerts,
    summary: {
      ...EMPTY_ALERT_STREAM_SUMMARY,
      total: scopedAlerts.length,
      active: counts.open + counts.acknowledged + counts.escalated,
      open: counts.open,
      acknowledged: counts.acknowledged,
      escalated: counts.escalated,
      resolved: counts.resolved,
    },
    connected,
    connectionStatus: !enabled ? 'disabled' : connected ? 'connected' : 'requires_operation',
    source: connected ? 'mock' : 'none',
    lastConnectedAt: connected ? now : null,
    lastEventAt: connected ? now : null,
    lastSyncAt: connected ? now : null,
    error: null,
    acknowledge,
    escalate,
    resolve,
    counts,
  };
}

// ─── useMockZones ─────────────────────────────────────────────────────────────

export interface MockZoneState {
  zones:   Zone[];
  loading: false;
  error:   null;
}

export function useMockZones(): MockZoneState {
  return { zones: MOCK_ZONES, loading: false, error: null };
}

// ─── Combined hooks ───────────────────────────────────────────────────────────
// Always call both mock and live hooks (React rules of hooks compliance),
// then select the result based on MockModeContext. Switching is instant —
// no reconnect, no reload.

export function useDensityData(): DensityStreamState {
  const { isMock } = useMockMode();
  const mock = useMockDensityStream();
  const live = useDensityStream();
  return isMock ? mock : live;
}

export function useAlertData(operationId?: string | null, options: AlertStreamOptions = {}): AlertStreamState {
  const { isMock } = useMockMode();
  const mock = useMockAlertStream(operationId, options);
  const live = useAlertStream(operationId, options);
  return isMock ? mock : live;
}
